import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Paper, Typography, Stack,
  InputAdornment, IconButton, Divider
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../Snackbar';
import { loginStaff } from '../../api/userApi';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';
import { brand } from '../../theme/tokens';


export default function AuthTabs() {
  const [showPwd, setShowPwd] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  const loginFormik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string().email().required('Required'),
      password: Yup.string().required('Required'),
    }),
    onSubmit: async (values) => {
      try {
        const res = await loginStaff(values);
        if (res.data.succeeded) {
          login(res.data.data.token, res.data.data);
          showSnackbar(res.data.messages[0], 'success');
          navigate('/dashboard');
        } else {
          showSnackbar(res.data.messages[0], 'error');
        }
      } catch (err) {
        showSnackbar(err.response?.data?.message || 'Login failed', 'error');
      }
    },
  });

  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  const pwdAdornment = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPwd((s) => !s)} edge="end" size="small">
        {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #fff5fa 0%, #fdeaf4 45%, #f6eefc 100%)',
      }}
    >
      {/* decorative blurred brand blobs */}
      <Box sx={{ position: 'absolute', top: -120, right: -100, width: 320, height: 320, borderRadius: '50%',
        background: `radial-gradient(circle, ${brand[500]}33, transparent 70%)`, filter: 'blur(8px)' }} />
      <Box sx={{ position: 'absolute', bottom: -140, left: -120, width: 360, height: 360, borderRadius: '50%',
        background: `radial-gradient(circle, ${brand[500]}26, transparent 70%)`, filter: 'blur(8px)' }} />

      <Paper
        elevation={0}
        sx={{
          width: '100%', maxWidth: 430, p: { xs: 3, sm: 4.5 }, borderRadius: 4,
          border: '1px solid', borderColor: 'rgba(234,37,139,0.12)',
          boxShadow: '0 20px 60px rgba(184,21,112,0.15)',
          backdropFilter: 'blur(4px)', backgroundColor: 'rgba(255,255,255,0.92)',
          position: 'relative', zIndex: 1,
        }}
      >
        <Stack alignItems="center" spacing={1.5} mb={2}>
          <Box
            sx={{
              width: 84, height: 84, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #ffffff, #fff0f7)',
              boxShadow: '0 8px 24px rgba(234,37,139,0.18)',
            }}
          >
            <img src={logo} alt="Vqura" style={{ height: 52, maxWidth: 64, objectFit: 'contain' }} />
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: 0.3 }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Sign in to continue to your clinic
          </Typography>
        </Stack>

        <form onSubmit={loginFormik.handleSubmit}>
            <TextField
              fullWidth label="Email" margin="normal" sx={fieldSx}
              {...loginFormik.getFieldProps('email')}
              error={loginFormik.touched.email && Boolean(loginFormik.errors.email)}
              helperText={loginFormik.touched.email && loginFormik.errors.email}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MailOutlineIcon fontSize="small" color="action" /></InputAdornment>) }}
            />
            <TextField
              fullWidth label="Password" margin="normal" sx={fieldSx}
              type={showPwd ? 'text' : 'password'}
              {...loginFormik.getFieldProps('password')}
              error={loginFormik.touched.password && Boolean(loginFormik.errors.password)}
              helperText={loginFormik.touched.password && loginFormik.errors.password}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>),
                endAdornment: pwdAdornment,
              }}
            />
            <Box display="flex" justifyContent="flex-end" mt={0.5}>
              <Button onClick={() => navigate('/forgot-password')} size="small" sx={{ textTransform: 'none' }}>
                Forgot Password?
              </Button>
            </Box>
            <Button
              type="submit" variant="contained" fullWidth size="large"
              disabled={loginFormik.isSubmitting}
              sx={{ mt: 2, py: 1.25, borderRadius: 2, fontWeight: 700,
              }}
            >
              {loginFormik.isSubmitting ? 'Signing in…' : 'Login'}
            </Button>
          </form>

        <Divider sx={{ my: 2.5 }}>
          <Typography variant="caption" color="text.secondary">or</Typography>
        </Divider>

        {/* Every account belongs to a clinic: you either start one or join one
            with its code. There is no longer a clinic-less "register". */}
        <Stack spacing={1.25}>
          <Button
            variant="outlined" fullWidth
            onClick={() => navigate('/signup')}
            sx={{ py: 1.1, borderRadius: 2, fontWeight: 600 }}
          >
            Create a clinic
          </Button>
          <Button
            variant="outlined" fullWidth
            onClick={() => navigate('/join')}
            sx={{ py: 1.1, borderRadius: 2, fontWeight: 600 }}
          >
            Join a clinic with a code
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
